import { useEffect, useRef } from 'react';
import '@google/model-viewer';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

interface Model3DViewerProps {
  url: string;
  fileType: string; // 소문자 glb/gltf/stl/obj
}

// glb/gltf는 model-viewer, stl/obj는 three.js로 렌더링
const Model3DViewer: React.FC<Model3DViewerProps> = ({ url, fileType }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isThree = fileType === 'stl' || fileType === 'obj';

  useEffect(() => {
    if (!isThree) return;
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let frameId = 0;
    const width = container.clientWidth || 600;
    const height = container.clientHeight || 280;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8fafc);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.0);
    keyLight.position.set(1, 1, 1);
    scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-1, -0.5, -1);
    scene.add(fillLight);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const fitAndAdd = (object: THREE.Object3D) => {
      if (disposed) return;
      const box = new THREE.Box3().setFromObject(object);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      object.position.sub(center);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      camera.position.set(maxDim * 0.8, maxDim * 0.8, maxDim * 2.0);
      camera.near = maxDim / 100;
      camera.far = maxDim * 100;
      camera.updateProjectionMatrix();
      controls.target.set(0, 0, 0);
      controls.update();
      scene.add(object);
    };

    if (fileType === 'stl') {
      new STLLoader().load(url, (geometry) => {
        if (disposed) return;
        geometry.computeVertexNormals();
        const material = new THREE.MeshStandardMaterial({ color: 0x9aa7b5, metalness: 0.15, roughness: 0.65 });
        fitAndAdd(new THREE.Mesh(geometry, material));
      });
    } else {
      new OBJLoader().load(url, (obj) => {
        if (!disposed) fitAndAdd(obj);
      });
    }

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = container.clientWidth || 600;
      const h = container.clientHeight || 280;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', onResize);
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [url, fileType, isThree]);

  if (isThree) {
    return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
  }

  return (
    <model-viewer
      src={url}
      camera-controls
      auto-rotate
      shadow-intensity="1"
      style={{ width: '100%', height: '100%', backgroundColor: '#F8FAFC' }}
    />
  );
};

export default Model3DViewer;
